import type { UserCapability } from '@joyful/utils'
import type {
  AppNavigationItem,
  AppNavigationLinkItem,
  AppRouteNavigationSection,
} from '../../../components/global-command/GlobalCommandCore'
import {
  getNavbarAppHref,
  NavbarAppSchema,
} from '../../../components/navbar/Navbar'
import type { NavbarApp } from '../../../components/navbar/Navbar'
import type { HasCapability } from '../../../hooks/useHasUserCapability'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import type { HasFeatureFlag } from '../../../hooks/useHasFeatureFlag'
import { useHasFeatureFlag } from '../../../hooks/useHasFeatureFlag'
import { useActiveAccount } from '../../../utils/activeAccountContext'
import type { FeatureFlag } from '../../../utils/featureFlags'
import { serializePracticeRequestsRouteQueryParams } from '../../requests/utils/practiceRequestsQueryParams'
import { WORK_BLOCKS_PAGE_ACCESS_CAPABILITIES } from '../../work-blocks/utils/workBlocksQueryParams'

export interface AppRouteDefinition {
  app: NavbarApp
  capability?: UserCapability | ReadonlyArray<UserCapability>
  capabilityMatch?: 'all' | 'any'
  featureFlag?: FeatureFlag | ReadonlyArray<FeatureFlag>
  key: string
  path: string
  scope?: 'account-param' | 'global' | 'org' | 'prefer-org'
  section: AppRouteNavigationSection
  title: string
}

export interface UseAppRouteNavigationItemsOptions {
  activeApp?: NavbarApp
  apps?: ReadonlyArray<NavbarApp>
  sections?: ReadonlyArray<AppRouteNavigationSection>
}

export interface GetDefaultAppRoutePathOptions {
  app: NavbarApp
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
  orgSlug: string | null
}

const AGENT_NAVIGATION_ITEM_KEYS: ReadonlyArray<string> = [
  'workspace:knowledge',
  'workspace:agent-proposals',
  'internal:agent-calls',
  'internal:credential-queues',
  'internal:prompts',
  'internal:agent-skills',
  'internal:agent-evaluation',
]

export const AppRouteRegistry: ReadonlyArray<AppRouteDefinition> = [
  {
    app: 'app',
    key: 'dashboard',
    path: '/dashboard',
    section: 'primary',
    title: 'Dashboard',
  },
  {
    app: 'app',
    capability: 'encounter.list.view',
    key: 'patients',
    path: '/patients',
    section: 'primary',
    title: 'Patients',
  },
  {
    app: 'app',
    capability: 'encounter.list.view',
    key: 'encounters',
    path: '/encounters',
    section: 'primary',
    title: 'Encounters',
  },
  {
    app: 'app',
    key: 'projects',
    path: '/projects',
    section: 'primary',
    title: 'Projects',
  },
  {
    app: 'app',
    key: 'requests',
    path: '/requests',
    section: 'primary',
    title: 'Requests',
  },
  {
    app: 'app',
    capability: 'dropbox.view',
    key: 'dropbox',
    path: '/dropbox',
    section: 'primary',
    title: 'Dropbox',
  },
  {
    app: 'app',
    capability: 'analysis.library.view',
    featureFlag: 'practice_library',
    key: 'library',
    path: '/library',
    section: 'primary',
    title: 'Library',
  },
  {
    app: 'app',
    capability: 'account.members.view',
    key: 'members',
    path: '/members',
    section: 'account',
    title: 'Manage team',
  },
  {
    app: 'app',
    key: 'account',
    path: '/account',
    scope: 'global',
    section: 'general',
    title: 'Account',
  },
  {
    app: 'app',
    capability: 'announcement.view.all',
    key: 'announcements',
    path: '/announcements',
    section: 'general',
    title: 'Announcements',
  },
  {
    app: 'workspace',
    capability: 'encounter.list.view',
    key: 'encounters',
    path: '/encounters',
    section: 'primary',
    title: 'Encounters',
  },
  {
    app: 'workspace',
    capability: [...WORK_BLOCKS_PAGE_ACCESS_CAPABILITIES],
    key: 'work-blocks',
    path: '/blocks/today',
    section: 'primary',
    title: 'Work blocks',
  },
  {
    app: 'workspace',
    capability: 'account_biller_assignment.manage',
    key: 'biller-assignments',
    path: '/members/assignments',
    section: 'account',
    title: 'Biller assignments',
  },
  {
    app: 'workspace',
    key: 'projects',
    path: '/projects',
    section: 'primary',
    title: 'Projects',
  },
  {
    app: 'workspace',
    key: 'requests',
    path: '/requests',
    section: 'primary',
    title: 'Requests',
  },
  {
    app: 'workspace',
    capability: 'dropbox.view',
    key: 'files',
    path: '/files',
    section: 'primary',
    title: 'Files',
  },
  {
    app: 'workspace',
    capability: 'agent.memories.view',
    key: 'knowledge',
    path: '/knowledge',
    section: 'primary',
    title: 'Memories',
  },
  {
    app: 'workspace',
    capability: 'automation.view',
    key: 'automations',
    path: '/automations',
    section: 'primary',
    title: 'Automations',
  },
  {
    app: 'workspace',
    capability: 'phone.view',
    key: 'phone',
    path: '/phone',
    section: 'primary',
    title: 'Phone',
  },
  {
    app: 'workspace',
    capability: 'credentials.manage',
    key: 'credentials',
    path: '/credentials',
    section: 'primary',
    title: 'Credentials',
  },
  {
    app: 'workspace',
    capability: 'logic_diagram.view',
    key: 'logic',
    path: '/logic',
    scope: 'global',
    section: 'primary',
    title: 'Trees',
  },
  {
    app: 'workspace',
    capability: ['agent.proposals.review', 'agent.proposals.view'],
    key: 'agent-proposals',
    path: '/internal/agent-proposals',
    scope: 'global',
    section: 'primary',
    title: 'Agent proposals',
  },
  {
    app: 'bpo',
    capability: [...WORK_BLOCKS_PAGE_ACCESS_CAPABILITIES],
    key: 'work-blocks',
    path: '/blocks/today',
    section: 'primary',
    title: 'Work blocks',
  },
  {
    app: 'bpo',
    capability: 'account_biller_assignment.manage',
    key: 'biller-assignments',
    path: '/members/assignments',
    section: 'primary',
    title: 'Biller assignments',
  },
  {
    app: 'bpo',
    capability: 'encounter.list.view',
    key: 'encounters',
    path: '/encounters',
    section: 'primary',
    title: 'Encounters',
  },
  {
    app: 'bpo',
    key: 'projects',
    path: '/projects',
    section: 'primary',
    title: 'Projects',
  },
  {
    app: 'bpo',
    capability: 'phone.view',
    key: 'phone',
    path: '/phone',
    section: 'primary',
    title: 'Phone',
  },
  {
    app: 'bpo',
    capability: ['dropbox.view', 'fax.view'],
    key: 'files',
    path: '/files',
    section: 'primary',
    title: 'Files',
  },
  {
    app: 'internal',
    key: 'dashboard',
    path: '/internal/dashboard',
    scope: 'prefer-org',
    section: 'primary',
    title: 'Dashboard',
  },
  {
    app: 'internal',
    capability: 'encounter_audit.review',
    key: 'audits',
    path: '/internal/audits/workblocks/today',
    scope: 'global',
    section: 'primary',
    title: 'Audits',
  },
  {
    app: 'internal',
    capability: 'admin.data_oncall.manage',
    key: 'data-oncall',
    path: '/internal/data-oncall',
    scope: 'global',
    section: 'primary',
    title: 'Data oncall',
  },
  {
    app: 'internal',
    capability: 'app.internal.view',
    key: 'editable-documents',
    path: '/internal/editable-documents',
    scope: 'account-param',
    section: 'account',
    title: 'Editable documents',
  },
  {
    app: 'internal',
    capability: 'dropbox.view',
    key: 'filesystem',
    path: '/internal/filesystem',
    scope: 'account-param',
    section: 'account',
    title: 'Filesystem',
  },
  {
    app: 'internal',
    capability: 'admin.capabilities.view',
    key: 'capabilities',
    path: '/internal/capabilities',
    scope: 'global',
    section: 'primary',
    title: 'Capabilities',
  },
  {
    app: 'internal',
    capability: 'admin.feature_flags.manage',
    key: 'feature-flags',
    path: '/internal/feature-flags',
    scope: 'global',
    section: 'primary',
    title: 'Feature flags',
  },
  {
    app: 'internal',
    capability: 'user.view.all',
    key: 'users',
    path: '/internal/users',
    scope: 'global',
    section: 'primary',
    title: 'Users',
  },
  {
    app: 'internal',
    capability: 'app.internal.view',
    key: 'ops-staffing',
    path: '/internal/ops-staffing',
    scope: 'global',
    section: 'primary',
    title: 'Ops staffing',
  },
  {
    app: 'internal',
    capability: 'admin.kpis.view',
    key: 'kpis',
    path: '/internal/kpis',
    scope: 'global',
    section: 'primary',
    title: 'KPIs',
  },
  {
    app: 'internal',
    capability: ['announcement.view.all', 'announcement.manage'],
    capabilityMatch: 'all',
    key: 'announcements',
    path: '/internal/announcements',
    scope: 'global',
    section: 'primary',
    title: 'Announcements',
  },
  {
    app: 'internal',
    capability: 'admin.agent_phone_calls.view',
    key: 'agent-calls',
    path: '/internal/agent-phone-calls',
    scope: 'global',
    section: 'primary',
    title: 'Agent calls',
  },
  {
    app: 'internal',
    capability: 'credentials.manage',
    key: 'credential-queues',
    path: '/internal/credentials/queue',
    scope: 'global',
    section: 'primary',
    title: 'Credential queues',
  },
  {
    app: 'internal',
    capability: 'encounter.work_blocks.manage',
    key: 'due-date-rules',
    path: '/internal/work-blocks/rules',
    scope: 'global',
    section: 'primary',
    title: 'Due-date rules',
  },
  {
    app: 'internal',
    capability: 'agent.prompts.view',
    key: 'prompts',
    path: '/internal/prompts',
    scope: 'global',
    section: 'primary',
    title: 'Prompts',
  },
  {
    app: 'internal',
    capability: 'admin.debug',
    key: 'feedback',
    path: '/internal/feedback',
    scope: 'global',
    section: 'primary',
    title: 'Feedback',
  },
  {
    app: 'internal',
    capability: 'agent.skills.view',
    key: 'agent-skills',
    path: '/internal/agent-skills',
    scope: 'global',
    section: 'primary',
    title: 'Agent skills',
  },
  {
    app: 'internal',
    capability: 'admin.invoices.view',
    key: 'invoices',
    path: '/internal/invoices',
    section: 'account',
    title: 'Invoices',
  },
  {
    app: 'internal',
    capability: 'agent.proposals.review',
    key: 'agent-evaluation',
    path: '/internal/agent-evaluation',
    scope: 'global',
    section: 'primary',
    title: 'Agent evaluation',
  },
  {
    app: 'internal',
    capability: 'kpi_email.view',
    key: 'kpi-emails',
    path: '/internal/kpi-emails',
    section: 'account',
    title: 'KPI emails',
  },
  {
    app: 'internal',
    capability: 'dashboard.internal.view',
    key: 'root-cause-themes',
    path: '/internal/root-cause-themes',
    section: 'account',
    title: 'Root-cause themes',
  },
  {
    app: 'internal',
    capability: 'analysis.view',
    key: 'analysis',
    path: '/internal/analysis',
    scope: 'org',
    section: 'account',
    title: 'Analysis',
  },
  {
    app: 'internal',
    capability: 'admin.debug',
    key: 'payer-directory',
    path: '/phone/payer-directory',
    section: 'account',
    title: 'Payer directory',
  },
]

export function useAppRouteNavigationItems({
  activeApp,
  apps,
  sections,
}: UseAppRouteNavigationItemsOptions = {}) {
  const activeAccount = useActiveAccount()
  const hasCapability = useHasUserCapability()
  const hasFeatureFlag = useHasFeatureFlag()

  return AppRouteRegistry.filter((definition) => {
    const isIncludedApp = apps ? apps.includes(definition.app) : true
    const isIncludedSection = sections
      ? sections.includes(definition.section)
      : true
    const appMeta = NavbarAppSchema.meta(definition.app)
    const isAppVisible =
      definition.app === activeApp || hasCapability(appMeta.capability)
    const isRouteVisible = isRouteDefinitionVisible({
      definition,
      hasCapability,
      hasFeatureFlag,
    })

    return isIncludedApp && isIncludedSection && isAppVisible && isRouteVisible
  })
    .map((definition) => {
      return createNavigationItem({
        activeApp,
        definition,
        orgSlug: activeAccount?.slug ?? null,
      })
    })
    .filter(Boolean)
}

export function useAgentRouteNavigationItems({
  activeApp,
}: {
  activeApp: NavbarApp
}) {
  return useAppRouteNavigationItems({
    activeApp,
    apps: ['workspace', 'internal'],
    sections: ['primary'],
  }).filter((item): item is AppNavigationLinkItem => {
    return item.path ? isAgentNavigationItem(item) : false
  })
}

export function createAgentNavigationGroupItem({
  children,
}: {
  children: ReadonlyArray<AppNavigationLinkItem>
}): AppNavigationItem | null {
  if (children.length === 0) {
    return null
  }

  return {
    items: children,
    key: 'agent',
    title: 'Agent',
    type: 'group',
  }
}

export function insertAgentNavigationGroupItem({
  agentNavigationGroup,
  insertAfterKey,
  navigationItems,
}: {
  agentNavigationGroup: AppNavigationItem | null
  insertAfterKey: string
  navigationItems: ReadonlyArray<AppNavigationItem>
}): Array<AppNavigationItem> {
  const nonAgentNavigationItems = navigationItems.filter((item) => {
    return !isAgentNavigationItem(item)
  })
  if (agentNavigationGroup === null) {
    return nonAgentNavigationItems
  }

  const insertAfterItem = nonAgentNavigationItems.find((item) => {
    return item.key === insertAfterKey
  })
  if (!insertAfterItem) {
    return nonAgentNavigationItems.concat(agentNavigationGroup)
  }

  return nonAgentNavigationItems.flatMap((item) => {
    return item.key === insertAfterKey ? [item, agentNavigationGroup] : [item]
  })
}

export function isAgentNavigationItem(item: AppNavigationItem) {
  return AGENT_NAVIGATION_ITEM_KEYS.includes(item.key)
}

export function getDefaultAppRoutePath({
  app,
  hasCapability,
  hasFeatureFlag,
  orgSlug,
}: GetDefaultAppRoutePathOptions) {
  const defaultDefinition = AppRouteRegistry.find((definition) => {
    const isTargetApp = definition.app === app
    const isPrimarySection = definition.section === 'primary'
    const isVisible = isRouteDefinitionVisible({
      definition,
      hasCapability,
      hasFeatureFlag,
    })

    return isTargetApp && isPrimarySection && isVisible
  })
  if (!defaultDefinition) {
    return '/'
  }

  const navigationItem = createNavigationItem({
    definition: defaultDefinition,
    orgSlug,
  })

  return navigationItem?.path ?? '/'
}

function isRouteDefinitionVisible({
  definition,
  hasCapability,
  hasFeatureFlag,
}: {
  definition: AppRouteDefinition
  hasCapability: HasCapability
  hasFeatureFlag: HasFeatureFlag
}) {
  if (definition.featureFlag !== undefined) {
    const featureFlags: ReadonlyArray<FeatureFlag> =
      typeof definition.featureFlag === 'string'
        ? [definition.featureFlag]
        : definition.featureFlag
    const hasRequiredFeatureFlag = featureFlags.some((featureFlag) => {
      return hasFeatureFlag(featureFlag)
    })
    if (!hasRequiredFeatureFlag) {
      return false
    }
  }

  if (definition.capability === undefined) {
    return true
  }

  const capabilities: ReadonlyArray<UserCapability> =
    typeof definition.capability === 'string'
      ? [definition.capability]
      : definition.capability

  if (definition.capabilityMatch === 'all') {
    return capabilities.every((capability) => {
      return hasCapability(capability)
    })
  }

  return capabilities.some((capability) => {
    return hasCapability(capability)
  })
}

function createNavigationItem({
  activeApp,
  definition,
  orgSlug,
}: {
  activeApp?: NavbarApp
  definition: AppRouteDefinition
  orgSlug: string | null
}): AppNavigationItem | null {
  const scope = definition.scope ?? 'org'

  if (orgSlug === null && (scope === 'org' || scope === 'account-param')) {
    return null
  }

  const path =
    scope === 'global' || (scope === 'prefer-org' && orgSlug === null)
      ? definition.path
      : scope === 'account-param'
        ? `${definition.path}/${orgSlug}`
        : `/${orgSlug}${definition.path}`
  const search =
    definition.key === 'requests'
      ? serializePracticeRequestsRouteQueryParams({ needs_response: true })
      : undefined
  const appMeta = NavbarAppSchema.meta(definition.app)
  const href =
    activeApp && activeApp !== definition.app
      ? getNavbarAppHref({
          app: definition.app,
          path: search ? `${path}${search}` : path,
        })
      : undefined

  return {
    description: appMeta.title,
    href,
    key: `${definition.app}:${definition.key}`,
    path,
    search,
    title: definition.title,
  }
}
